import React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Check, Shield, X } from 'lucide-react';
import AutoMode from '@mui/icons-material/AutoMode';
import ChargerImageCarousel from '@/components/ev/ChargerImageCarousel';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };
const sans = { fontFamily: '"DM Sans", "Space Grotesk", sans-serif' };

function SpecRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-2 border-b border-gray-100 last:border-0" style={sans}>
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}

function FeatureCheck({ children }) {
  return (
    <div className="flex items-start gap-2 text-sm text-gray-700" style={sans}>
      <Check className="w-4 h-4 text-[#314A56] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
      <span>{children}</span>
    </div>
  );
}

function IncludedBadge({ children, variant = 'green' }) {
  const cls = variant === 'yellow'
    ? 'bg-[#F2E7BF] text-[#B58716] border-[#D4A62A]/30'
    : 'bg-[#F6F3EC] text-[#314A56] border-[#D9D2C5]/60';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${cls}`} style={sans}>
      {children}
    </span>
  );
}

/**
 * CatalogChargerInfoModal — fully data-driven "Mere info" modal.
 *
 * Reads EVERYTHING from the live Charger catalog record (admin-editable):
 * badge, status, tagline/brand_subtext, image, gallery_images, description,
 * key_features, specs and meter note. So whatever is set in /admin/opladere
 * rolls out here automatically (NyCheckout + EVChargerStep).
 */
export default function CatalogChargerInfoModal({ charger, open, onClose }) {
  if (!charger) return null;

  const isSoldOut = charger.status === 'sold_out';
  const isComingSoon = charger.status === 'coming_soon';

  // Gallery: prefer the admin gallery, fall back to the lifestyle image.
  const gallery = (charger.gallery_images || []).filter((g) => g?.url);
  const fallbackGallery = !gallery.length && charger.lifestyle_image
    ? [{ url: charger.lifestyle_image, alt: charger.name, caption: charger.name }]
    : [];
  const images = gallery.length ? gallery : fallbackGallery;

  const specs = (charger.specs || []).filter((s) => s && (s.label || s.value));
  const keyFeatures = (charger.key_features?.length ? charger.key_features : charger.features) || [];
  const subtitle = charger.brand_subtext || charger.tagline || charger.short_desc || '';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white rounded-2xl p-0 [&>button:last-child]:hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#2F424D] to-[#1F2E33] px-5 sm:px-6 pt-5 pb-5 sm:pb-6 text-white rounded-t-2xl">
          <button onClick={() => onClose(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-xl p-2 flex items-center justify-center flex-shrink-0">
              <img src={charger.image} alt={charger.name} className={`w-full h-full object-contain ${charger.image_scale || ''}`} />
            </div>
            <div>
              {charger.badge && (
                <span className="inline-block text-[9px] font-bold px-2.5 py-0.5 rounded-full mb-1.5 bg-[#F2E7BF] text-[#B58716]">{charger.badge}</span>
              )}
              <h3 className="text-xl font-bold" style={serif}>{charger.name}</h3>
              {subtitle && <p className="text-xs text-white/60 mt-0.5" style={sans}>{subtitle}</p>}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-6 space-y-5" style={sans}>
          {/* Status banner — reflects admin stock status */}
          {isSoldOut && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
              <span className="inline-block text-xs font-bold px-3 py-1 rounded-full bg-red-600 text-white">Udsolgt</span>
              <p className="text-xs text-red-700 mt-2 font-medium">{charger.name} er midlertidigt udsolgt og kan ikke bestilles.</p>
            </div>
          )}
          {isComingSoon && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
              <span className="inline-block text-xs font-bold px-3 py-1 rounded-full bg-orange-500 text-white">Snart på lager · bestil nu</span>
              <p className="text-xs text-orange-700 mt-2 font-medium">Bestil nu – leveres så snart den er på lager igen.</p>
            </div>
          )}

          {images.length > 0 && <ChargerImageCarousel images={images} />}

          {charger.description && <p className="text-sm text-gray-600">{charger.description}</p>}

          {keyFeatures.length > 0 && (
            <div>
              <h4 className="font-bold text-gray-900 text-sm mb-3" style={serif}>Nøglefunktioner</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {keyFeatures.map((f, i) => <FeatureCheck key={i}>{f}</FeatureCheck>)}
              </div>
            </div>
          )}

          {(charger.meter_note_title || charger.meter_note_body) && (
            <div className="bg-[#F6F3EC] border border-[#D4A62A]/20 rounded-xl p-4">
              {charger.meter_note_title && <p className="text-sm text-[#21343D] font-semibold mb-1">✓ {charger.meter_note_title}</p>}
              {charger.meter_note_body && <p className="text-xs text-gray-600">{charger.meter_note_body}</p>}
            </div>
          )}

          {specs.length > 0 && (
            <div>
              <h4 className="font-bold text-gray-900 text-sm mb-3" style={serif}>Specifikationer</h4>
              <div className="bg-gray-50 rounded-xl p-4 space-y-0">
                {specs.map((s, i) => <SpecRow key={i} label={s.label} value={s.value} />)}
              </div>
            </div>
          )}

          <div>
            <h4 className="font-bold text-gray-900 text-sm mb-3" style={serif}>Inkluderet med enkel energi</h4>
            <div className="flex flex-wrap gap-2">
              <IncludedBadge><AutoMode sx={{ fontSize: 14 }} /> Saving Mode 0 kr./md.</IncludedBadge>
              <IncludedBadge><Shield className="w-3.5 h-3.5" /> Serviceaftale 0 kr./md.</IncludedBadge>
              <IncludedBadge variant="yellow">10 øre laderabat/kWh</IncludedBadge>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-600"><strong>Installation:</strong> Vælg installationstype i næste trin – installation skal udføres af autoriseret el-installatør.</p>
          </div>

          <button onClick={() => onClose(false)} className="w-full py-3 rounded-xl bg-[#F4F1EA] hover:bg-[#EDE8DF] text-sm font-semibold text-[#394D59] border border-[#D9D2C5]/50 transition-colors">Luk</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}